import * as Ar from './ar';
import { Placement } from '@dora/arctic';
import * as Preact from 'preact';
import * as H from 'preact/hooks';
import { AppControlContext } from '../context/appControl';
import { useModelViewer } from '../modelViewer/useModelViewer';
import { useDispatch, useSelector } from '../hooks/redux';
import * as Navigation from '../../store/actions/navigation';
import * as Selectors from '../../store/selectors';
import { logError } from '../../store/actions/logging';
import * as O from 'fp-ts/Option';
import { pipe } from 'fp-ts/lib/function';
import { useAsync } from '../hooks/useAsync';
import { IS_ANDROID } from '@google/model-viewer/lib/constants';
import axios from 'axios';
import { ModelRequest } from './modelRequest';
import { TranslateContext } from '@denysvuika/preact-translate';
import { useTheme } from '@mui/material';

export type Props = {
    onReturn: () => void;
    getModelRequest: () => O.Option<ModelRequest>;
    getGltfBlob: () => O.Option<Promise<Blob>>;
    returnToPlanner: string;
    placement: Placement;
};

export const Component = (props: Props) => {
    const arCompatibility = useSelector(Selectors.Core.arCompatibility);
    const modelGeneratorUrl = useSelector(Selectors.Core.modelGeneratorUrl);
    const tenantIdentifier = useSelector(Selectors.Core.tenantIdentifier);
    const { t } = H.useContext(TranslateContext);
    const arModalText = {
        incompatibleDeviceDialogText: t('i18n.global.incompatibleDeviceDialogHeader'),
        arErrorDialogText: t('i18n.global.arErrorDialogHeader'),
        buttonText: t('i18n.global.buttonText'),
        buttonExplainerText: t('i18n.global.buttonExplainerText'),
    };
    const dispatch = useDispatch();

    const modelUrl = useAsync(async () => {
        // Android must have a concrete URL, let's fetch one from our services
        if (IS_ANDROID) {
            return pipe(
                await props.getModelRequest(),
                O.map(async request => {
                    const response = await axios.post(
                        `${modelGeneratorUrl}/api/v1/${tenantIdentifier}/model-generate`,
                        request,
                        {
                            responseType: 'text',
                        },
                    );
                    return response.data as string;
                }),
                O.getOrElse((): Promise<string | undefined> => Promise.resolve(undefined)),
            );
        }

        // iOS uses a model generated from the scene as a blob url
        else {
            return pipe(
                await props.getGltfBlob(),
                O.map(async result => {
                    const blob = await result;
                    return URL.createObjectURL(blob);
                }),
                O.getOrElse((): Promise<string | undefined> => Promise.resolve(undefined)),
            );
        }
    }, [props.getGltfBlob, props.getModelRequest]);

    const theme = useTheme();
    const modelViewer = useModelViewer(false, true, theme, modelUrl.result, props.placement);
    const appControl = H.useContext(AppControlContext);

    if (arModalText == undefined) {
        dispatch(logError('Displaying Inspiration Runway while not in Inspiration Mode'));
        return null;
    }

    return (
        <Ar.Component
            activate={() => {
                dispatch(Navigation.viewInArAction({ mode: 'advanced' }));
                modelViewer.activate();
            }}
            generateUSDZ={() => {
                dispatch(Navigation.viewInArAction({ mode: 'advanced' }));
                return modelViewer.generateUSDZ();
            }}
            error={modelViewer.error ? modelViewer.error : undefined}
            closeApp={() => appControl?.closeApp()}
            loading={!modelUrl.result}
            arCompatibility={arCompatibility}
            returnButton={{ text: props.returnToPlanner, onClick: props.onReturn }}
            arButtonText={arModalText.buttonText}
            arErrorDialogHeader={arModalText.arErrorDialogText}
            arButtonExplainerText={arModalText.buttonExplainerText}
            arIncompatibleDeviceDialogHeader={arModalText.incompatibleDeviceDialogText}
        />
    );
};
